import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { fetchPatientsList } from '~/backend/connector-next-api/connector-next-components';
import { useConnectorNextContext } from '~/backend/connector-next-api/connector-next-context';
import type { PatientSummaryDto } from '~/backend/connector-next-api/connector-next-schemas';
import {
    IMPORT_PROGRESS_POLL_MS,
    patientFilterQueryParams,
    patientImportActivity,
    sortDirectionParam,
    sortKeyParam,
    type PatientFilters,
    type Sort,
} from '../model';

/**
 * connector-next caps a page at 200 patients (PatientListQuery.MaxPageSize) and
 * answers 400 above it, so reaching the rest of a larger cohort means walking
 * the pages.
 */
const PAGE_SIZE = 200;

type PatientListQuery = {
    /** Free-text patient search, or undefined for the whole cohort. */
    search: string | undefined;
    sort: Sort;
    /** Server-side filters; an unfiltered dimension is simply not sent. */
    filters: PatientFilters;
};

export type PatientListResult = {
    /** Every patient loaded so far, in the server's order. */
    patients: PatientSummaryDto[];
    /** Patients matching the query server-side — not the number loaded. */
    totalCount: number;
    hasMore: boolean;
    loadMore: () => void;
    isLoadingMore: boolean;
    isLoading: boolean;
    /**
     * A search/sort change is in flight over an already-rendered list — not the
     * first load, and not a next page, both of which have their own indicator.
     */
    isRefreshing: boolean;
    isError: boolean;
    error: unknown;
};

/**
 * The patient list, walked one page at a time. Search, sort and filters reset
 * the walk; loadMore appends the next page to what is already shown.
 */
export function usePatientList({
    search,
    sort,
    filters,
}: PatientListQuery): PatientListResult {
    const { fetcherOptions, queryOptions } = useConnectorNextContext();

    const query = useInfiniteQuery({
        queryKey: ['api', 'patients', { search, sort, filters }],
        queryFn: ({ pageParam, signal }) =>
            fetchPatientsList(
                {
                    ...fetcherOptions,
                    queryParams: {
                        page: pageParam,
                        pageSize: PAGE_SIZE,
                        search,
                        sort: sortKeyParam(sort.key),
                        direction: sortDirectionParam(sort.direction),
                        ...patientFilterQueryParams(filters),
                    },
                },
                signal
            ),
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => {
            // An empty page ends the walk even if totalCount disagrees, so a
            // miscount cannot spin the list forever.
            if ((lastPage.items?.length ?? 0) === 0) {
                return undefined;
            }
            const loaded = allPages.reduce(
                (count, page) => count + (page.items?.length ?? 0),
                0
            );
            return loaded < (lastPage.totalCount ?? 0)
                ? allPages.length + 1
                : undefined;
        },
        // Keep the previous list mounted across sort/search so it doesn't blank
        // to a spinner; a lightweight overlay signals the background refetch.
        placeholderData: keepPreviousData,
        // A row with series still arriving changes without anyone acting, so the
        // list re-reads itself until every loaded row has settled.
        refetchInterval: (current) =>
            current.state.data?.pages.some((page) =>
                page.items?.some(
                    (patient) => patientImportActivity(patient).inFlight > 0
                )
            )
                ? IMPORT_PROGRESS_POLL_MS
                : false,
        ...queryOptions,
    });

    const { fetchNextPage } = query;
    const isFetching = query.isFetching;
    const loadMore = useCallback(() => {
        // Any in-flight fetch — a next page or a search/sort refresh over the
        // placeholder-kept list — shares the query's single fetch, so firing
        // another would cancel or overwrite it.
        if (isFetching) {
            return;
        }
        void fetchNextPage();
    }, [fetchNextPage, isFetching]);

    const pages = query.data?.pages;
    const patients = useMemo(
        () => (pages ?? []).flatMap((page) => page.items ?? []),
        [pages]
    );

    return {
        patients,
        // The latest page's count is the one getNextPageParam trusts; the UI
        // must agree or the footer can claim patients are missing after the
        // walk ends.
        totalCount: pages?.[pages.length - 1]?.totalCount ?? patients.length,
        hasMore: query.hasNextPage,
        loadMore,
        isLoadingMore: query.isFetchingNextPage,
        isLoading: query.isLoading,
        isRefreshing:
            query.isFetching && !query.isLoading && !query.isFetchingNextPage,
        isError: query.isError,
        error: query.error,
    };
}
